import { Alert as MuiAlert, Snackbar } from '@mui/material'
import { SnackbarCloseReason } from '@mui/material/Snackbar/useSnackbar.types'
import React from 'react'

interface AlertProps {
  duration?: number
  isOpen: boolean
  message: string
  severity: 'error' | 'info' | 'success' | 'warning'
  onClose: () => void
}

/*
 * Alert component to display messages to the user. This component makes use of the AlertContext to manage
 * the state of the alert.
 *
 * @param {number} duration - The duration for which the alert will be displayed.
 * @param {boolean} isOpen - Indicates if the alert is open.
 * @param {string} message - The message to display in the alert.
 * @param {'error' | 'info' | 'success' | 'warning'} severity - The severity level of the alert.
 * @param {function} onClose - Function to call when the alert is closed.
 */
const Alert: React.FC<AlertProps> = ({ duration = 5000, isOpen, message, severity, onClose }) => {
  const handleClose = (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    // prevent clickaway from closing the alert because otherwise it seems to
    // make useFetch trigger callFetch twice instead of once
    if (reason === 'clickaway') {
      return
    }
    onClose()
  }

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={duration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MuiAlert
        aria-live={severity === 'error' ? 'assertive' : 'polite'}
        role={severity === 'error' ? 'alert' : 'status'}
        onClose={onClose}
        severity={severity}
        sx={{ width: '100%' }}
      >
        {message}
      </MuiAlert>
    </Snackbar>
  )
}

export default Alert
